import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import qs from 'qs';

import redirectToActivate from '../redirectToActivate';
import * as actions from '../actions';
import * as commonActions from '../../common/actions';
import * as FtuxWalkthrough from '../../common/data/FtuxWalkthrough';

import { BrandBand } from '../../common/components';
import {
  DashboardHeader,
  DashboardBody,
  CreateDataExtensionModal,
  UpdateDataExtensionModal
} from '../components';
import { WelcomeMat } from '../../common/components';
import { shouldShowWelcomeMat } from '../../common/data/WelcomeMat';
import { Spinner } from '@salesforce/design-system-react';
import mcLib from '../../utilities/mc-lib';
import * as Constants from '../constants';

import './Dashboard.scss';

export class Dashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      setUpAppIsProvisioned: false
    };
  }

  componentDidMount() {
    this.getActivation();
    const parsedQueryString = qs.parse(this.props.location.search.substr(1));
    const options = { ...parsedQueryString };
    this.props.actions.submitEEFV2ProvisionedStatus(options);
    this.getAppProvisionedStatus(Constants.SETUP_APP, 'useMockSetup').then(
      setUpAppIsProvisioned => this.setState({ setUpAppIsProvisioned })
    );
  }

  componentDidUpdate(prevProps) {
    const parsedQueryString = qs.parse(this.props.location.search.substr(1));
    let options = {
      ...parsedQueryString,
      onError: redirectToActivate(this.props.history)
    };
    const {
      activation,
      dataExtension,
      einsteinRecommendations
    } = this.props.frequency;
    const { dataFilter } = dataExtension;

    // if activation succeeds, kickoff dashboard data fetch
    if (
      prevProps.frequency.activation.isLoading !== activation.isLoading &&
      !activation.error &&
      !activation.isLoading
    ) {
      // likely the source of the infinite loop erroring
      options.onError = redirectToActivate(this.props.history);
      this.props.actions.getDataExtension(parsedQueryString);
      this.props.actions.getEinsteinRecommendations(options, dataFilter);
    }

    // updateFrequencyRecs in state after call is received from API
    if (
      prevProps.frequency.einsteinRecommendations.isLoading !==
      einsteinRecommendations.isLoading
    ) {
      if (!einsteinRecommendations.isLoading) {
        this.props.actions.updateFrequencyRecs(einsteinRecommendations.data);
      }
    }

    // update chart if sidebar values change
    if (
      prevProps.frequency.dataExtension.dataFilter !== dataExtension.dataFilter
    ) {
      const oldDataFilter = prevProps.frequency.dataExtension.dataFilter;
      const newDataFilter = dataExtension.dataFilter;
      // get new einsteinFrequencyRecs if lookback, emailType, rate, or range start/end change
      if (
        oldDataFilter.lookback !== newDataFilter.lookback ||
        oldDataFilter.emailType !== newDataFilter.emailType ||
        oldDataFilter.eventType !== newDataFilter.eventType ||
        oldDataFilter.rangeStart !== newDataFilter.rangeStart ||
        oldDataFilter.rangeEnd !== newDataFilter.rangeEnd
      ) {
        this.props.actions.getEinsteinRecommendations(options, newDataFilter);
      } else if (
        // only monitor clickRateWeight since open/clickRateWeight are complimentary
        // and clickRateWeight is updated *after* openRateWeight (so we don't make multiple calls)
        oldDataFilter.clickRateWeight !== newDataFilter.clickRateWeight ||
        oldDataFilter.unsubscribePenaltyFactor !==
          newDataFilter.unsubscribePenaltyFactor
      ) {
        this.props.actions.getEinsteinRecommendations(options, newDataFilter);
      }

      this.props.actions.updateChartParams(dataExtension.dataFilter);
    }

    // call frequencies API if chartParams are updated
    if (
      prevProps.frequency.dataExtension.chartParams !==
      dataExtension.chartParams
    ) {
      // wait for Einstein Recs response first
      // regardless of new recs or not, this will always be called after frequency Recs
      // response completes (I think?)
      if (!einsteinRecommendations.isLoading) {
        this.props.actions.getFrequenciesData(
          options,
          dataExtension.chartParams
        );
      }
    }

    if (
      this.props.ftux.currentStep < 1 &&
      FtuxWalkthrough.shouldShowFtuxWalkthrough() &&
      !shouldShowWelcomeMat('frequencyWelcomeMat')
    ) {
      this.props.actions.startWalkthrough();
    }
  }

  getActivation() {
    const queryString = this.props.location.search.substr(1);
    const parsedQueryString = qs.parse(queryString);
    const options = {
      ...parsedQueryString,
      onError: redirectToActivate(this.props.history, queryString)
    };
    this.props.actions.getActivationStatus(options);
  }

  renderSpinner() {
    return (
      <div>
        <Spinner variant="brand" />
      </div>
    );
  }

  renderDataExtensionModal() {
    return this.props.frequency.dataExtension.exists ? (
      <UpdateDataExtensionModal
        dataExtension={this.props.frequency.dataExtension}
        {...this.props}
      />
    ) : (
      <CreateDataExtensionModal
        dataExtension={this.props.frequency.dataExtension}
        {...this.props}
      />
    );
  }

  getAppProvisionedStatus(appName, mockParamName) {
    const queryString = this.props.location.search.substr(1);
    const parsedQueryString = qs.parse(queryString);
    const options = {
      ...parsedQueryString
    };
    return new Promise(resolve => {
      if (options.hasOwnProperty(mockParamName)) {
        resolve(options[mockParamName] === 'true');
      }
      mcLib.getAppInfo({ appName }, data => {
        let appStatus = false;
        if (data.hasOwnProperty('err')) {
          console.log(data.err);
        } else {
          appStatus = data.appInfo.hasAccess && data.appInfo.isProvisioned;
        }
        resolve(appStatus);
      });
    });
  }

  renderWelcomeMatAndToast() {
    const name = this.props.eefV2IsProvisioned
      ? 'deprecationModal'
      : 'frequencyWelcomeMat';
    return (
      <WelcomeMat
        name={name}
        eefV2IsProvisioned={this.props.eefV2IsProvisioned}
        setUpAppIsProvisioned={this.state.setUpAppIsProvisioned}
        {...this.props}
      />
    );
  }

  renderPage() {
    return (
      <BrandBand>
        <DashboardHeader
          actions={this.props.actions}
          location={this.props.location}
          ftux={this.props.ftux}
          {...this.props.frequency}
        />
        <DashboardBody
          actions={this.props.actions}
          location={this.props.location}
          ftux={this.props.ftux}
          {...this.props.frequency}
          modals={this.props.modals}
        />
        {this.renderWelcomeMatAndToast()}
        {this.renderDataExtensionModal()}
      </BrandBand>
    );
  }

  render() {
    const eefStatusIsLoading = this.props.eefV2IsProvisioned === undefined;
    const { isLoading } = this.props.frequency.frequencies;
    return (
      <div>
        {isLoading || eefStatusIsLoading
          ? this.renderSpinner()
          : this.renderPage()}
      </div>
    );
  }
}

Dashboard.propTypes = {
  children: PropTypes.element,
  actions: PropTypes.object,
  location: PropTypes.object,
  frequency: PropTypes.object,
  ftux: PropTypes.object,
  modals: PropTypes.object.isRequired
};

function mapStateToProps(state) {
  return {
    frequency: state.frequency,
    ftux: state.ftux,
    modals: state.modals,
    welcomeMat: state.welcomeMat,
    eefV2IsProvisioned: state.frequency.eefV2Provisioning.eefV2IsProvisioned
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...actions, ...commonActions }, dispatch)
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Dashboard);
